import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const PAYMENT_MODES = ['cash', 'cheque'];

const ExpenseFormModal = ({ expense, onClose, onSaved }) => {
  const isEdit = Boolean(expense);

  const [expenseTypes, setExpenseTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    expense_type_id: '',
    scope: 'general',
    product_id: '',
    amount: '',
    payment_mode: 'cash',
    bank_name: '',
    cheque_number: '',
    paid_by: '',
    paid_to: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [typesRes, prodRes] = await Promise.all([
          chemicalService.getExpenseTypes({ page: 1, limit: 500, is_active: true }),
          chemicalService.getProducts({ page: 1, limit: 1000 }),
        ]);
        setExpenseTypes(typesRes.data || []);
        setProducts(prodRes.data || []);
      } catch (err) {
        console.error('Error loading data:', err);
        alert('❌ Failed to load expense types or products');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (expense) {
      setForm({
        expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : new Date().toISOString().split('T')[0],
        expense_type_id: expense.expense_type_id || '',
        scope: expense.scope || 'general',
        product_id: expense.product_id ? String(expense.product_id) : '',
        amount: expense.amount != null ? expense.amount : '',
        payment_mode: expense.payment_mode || 'cash',
        bank_name: expense.bank_name || '',
        cheque_number: expense.cheque_number || '',
        paid_by: expense.paid_by || '',
        paid_to: expense.paid_to || '',
        description: expense.description || '',
      });
    }
  }, [expense]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.expense_date) return alert('❌ Date is required.');
    if (!form.expense_type_id) return alert('❌ Select an expense type.');
    if (!form.amount || parseFloat(form.amount) <= 0) return alert('❌ Amount must be > 0.');
    if (form.scope === 'product' && !form.product_id) return alert('❌ Select a product for a product-specific expense.');
    if (form.payment_mode === 'cheque') {
      if (!form.bank_name.trim()) return alert('❌ Bank Name is required for cheque payments.');
      if (!form.cheque_number.trim()) return alert('❌ Cheque Number is required for cheque payments.');
    }

    try {
      setSubmitting(true);
      const payload = {
        expense_date: form.expense_date,
        expense_type_id: parseInt(form.expense_type_id, 10),
        scope: form.scope,
        product_id: form.scope === 'product' ? parseInt(form.product_id, 10) : null,
        amount: parseFloat(form.amount),
        payment_mode: form.payment_mode,
        bank_name: form.payment_mode === 'cheque' ? (form.bank_name.trim() || null) : null,
        cheque_number: form.payment_mode === 'cheque' ? (form.cheque_number.trim() || null) : null,
        paid_by: form.paid_by.trim() || null,
        paid_to: form.paid_to.trim() || null,
        description: form.description.trim() || null,
      };

      if (isEdit) {
        await chemicalService.updateExpense(expense.id, payload);
        alert('✅ Expense updated successfully!');
      } else {
        await chemicalService.createExpense(payload);
        alert('✅ Expense recorded successfully!');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save expense';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>{isEdit ? '✏️ Edit Expense' : '💸 New Expense'}</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {isEdit ? 'Updating expense entry' : 'Record an operational expense'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* Scope toggle */}
            <div style={styles.sectionLabel}>Expense Scope</div>
            <div style={styles.scopeTabs}>
              {[
                { id: 'general', label: '🏢 General', desc: 'Rent, utilities, salaries' },
                { id: 'product', label: '📦 Product-specific', desc: 'Tied to one product' },
              ].map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handleChange('scope', s.id)}
                  style={{
                    ...styles.scopeButton,
                    borderColor: form.scope === s.id ? '#e74c3c' : '#e0e0e0',
                    background: form.scope === s.id ? '#fdecea' : 'white',
                  }}
                >
                  <div style={{ fontWeight: 700, color: form.scope === s.id ? '#e74c3c' : '#2c3e50' }}>{s.label}</div>
                  <div style={{ fontSize: 11, color: '#95a5a6', marginTop: 2 }}>{s.desc}</div>
                </button>
              ))}
            </div>

            {form.scope === 'product' && (
              <div style={{ ...styles.formGroup, marginTop: 12 }}>
                <label style={styles.label}>Product <span style={styles.required}>*</span></label>
                <select
                  value={form.product_id}
                  onChange={(e) => handleChange('product_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loading}
                >
                  <option value="">{loading ? 'Loading...' : '-- Select product --'}</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                  ))}
                </select>
                <span style={styles.hint}>This expense will appear in the selected product's ledger.</span>
              </div>
            )}

            <div style={styles.sectionLabel}>Details</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Date <span style={styles.required}>*</span></label>
                <input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => handleChange('expense_date', e.target.value)}
                  onClick={(e) => e.target.showPicker?.()}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Expense Type <span style={styles.required}>*</span></label>
                <select
                  value={form.expense_type_id}
                  onChange={(e) => handleChange('expense_type_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loading}
                >
                  <option value="">
                    {loading ? 'Loading...' : expenseTypes.length === 0 ? '-- No active expense types. Add some in Settings tab. --' : '-- Select type --'}
                  </option>
                  {expenseTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (Rs) <span style={styles.required}>*</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  style={styles.input}
                  placeholder="0"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Mode <span style={styles.required}>*</span></label>
                <select
                  value={form.payment_mode}
                  onChange={(e) => handleChange('payment_mode', e.target.value)}
                  style={styles.input}
                  required
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>{m === 'cash' ? 'Cash' : 'Cheque'}</option>
                  ))}
                </select>
              </div>
            </div>

            {form.payment_mode === 'cheque' && (
              <div style={styles.row}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Bank Name <span style={styles.required}>*</span></label>
                  <input
                    type="text"
                    value={form.bank_name}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    style={styles.input}
                    placeholder="e.g. HBL, Meezan Bank"
                    required
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cheque Number <span style={styles.required}>*</span></label>
                  <input
                    type="text"
                    value={form.cheque_number}
                    onChange={(e) => handleChange('cheque_number', e.target.value)}
                    style={styles.input}
                    placeholder="e.g. 123456"
                    required
                  />
                </div>
              </div>
            )}

            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Paid By</label>
                <input
                  type="text"
                  value={form.paid_by}
                  onChange={(e) => handleChange('paid_by', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Owner, Cash Account, Zeeshan"
                />
                <span style={styles.hint}>Who paid this expense</span>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Paid To</label>
                <input
                  type="text"
                  value={form.paid_to}
                  onChange={(e) => handleChange('paid_to', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. K-Electric, SNGPL, ABC Supplies"
                />
                <span style={styles.hint}>Who/where the money went</span>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description (optional)</label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                style={{ ...styles.input, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="e.g. Electricity bill May 2026, Office cleaning supplies, etc."
                rows="2"
              />
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>Cancel</button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : (isEdit ? '💾 Update Expense' : '💾 Record Expense')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '760px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '18px', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0' },
  scopeTabs: { display: 'flex', gap: '12px' },
  scopeButton: { flex: 1, padding: '12px 14px', border: '2px solid', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', background: 'white' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: { flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  hint: { fontSize: '11px', color: '#95a5a6', fontStyle: 'italic' },
  modalFooter: { padding: '16px 30px', borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', position: 'sticky', bottom: 0, background: 'white' },
  cancelButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { padding: '12px 28px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default ExpenseFormModal;